import { apiSlice } from './apiSlice';
import { Route } from '../../types';
import type { FinanceUserBrief } from './financeApi';

export interface CargoLocation {
  id: number;
  routeId: number;
  cargoId: number;
  latitude: number;
  longitude: number;
  address?: string;
  recordedAt: string;
  createdAt: string;
  createdBy: number;
  cargo?: {
    id: number;
    name: string;
    description?: string;
  };
  user?: FinanceUserBrief | null;
  route?: Pick<Route, 'id' | 'transportType' | 'status' | 'loadingAddress' | 'unloadingAddress' | 'actualLoadingDate' | 'actualUnloadingDate'>;
}

export interface CreateLocationRequest {
  routeId: number;
  cargoId: number;
  latitude: number;
  longitude: number;
  address?: string;
  recordedAt: string;
}

export interface UpdateLocationRequest {
  id: number;
  latitude: number;
  longitude: number;
  address?: string;
  recordedAt: string;
}

export interface RouteCurrentLocation {
  id: number;
  currentLocationLat: number | null;
  currentLocationLng: number | null;
  currentLocationUpdatedAt: string | null;
}

export const locationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Получить все местоположения для маршрута
    getRouteLocations: builder.query<CargoLocation[], number>({
      query: (routeId) => `/locations/route/${routeId}`,
      providesTags: (result, error, routeId) => [
        { type: 'RouteLocation', id: routeId },
        ...(result?.map(({ id }) => ({ type: 'Location' as const, id })) ?? [])
      ],
    }),

    // Получить все местоположения для груза
    getCargoLocations: builder.query<CargoLocation[], number>({
      query: (cargoId) => `/locations/cargo/${cargoId}`,
      providesTags: (result, error, cargoId) => [
        { type: 'Location' as const, id: `cargo-${cargoId}` },
        ...(result?.map(({ id }) => ({ type: 'Location' as const, id })) ?? [])
      ],
    }),

    // Получить текущее местоположение маршрута
    getCurrentRouteLocation: builder.query<RouteCurrentLocation, number>({
      query: (routeId) => `/locations/route/${routeId}/current`,
      providesTags: (result, error, routeId) => [
        { type: 'RouteLocation', id: routeId }
      ],
    }),

    // Создать новое местоположение
    createLocation: builder.mutation<CargoLocation, CreateLocationRequest>({
      query: (location) => ({
        url: '/locations',
        method: 'POST',
        body: location,
      }),
      invalidatesTags: (result, error, { routeId, cargoId }) => [
        { type: 'RouteLocation', id: routeId },
        { type: 'Location', id: `cargo-${cargoId}` },
        'Location'
      ],
    }),

    // Обновить местоположение
    updateLocation: builder.mutation<CargoLocation, UpdateLocationRequest>({
      query: ({ id, ...location }) => ({
        url: `/locations/${id}`,
        method: 'PUT',
        body: location,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Location', id },
        'Location'
      ],
    }),

    // Удалить местоположение
    deleteLocation: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/locations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Location', id },
        'Location'
      ],
    }),
  }),
});

export const {
  useGetRouteLocationsQuery,
  useGetCargoLocationsQuery,
  useGetCurrentRouteLocationQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
} = locationsApi; 